'use client';

/**
 * Företagspriset per rad i varukorg och kassa.
 *
 * Varukorgen lagrar listpriset inkl. moms (products.price). Det riktiga priset
 * räknas av API:ets foretagsPris() — lägsta av företagets rabatterade pris och
 * en pågående kampanj, aldrig rabatt ovanpå kampanj. Hämtas vid visning, så att
 * en kampanj som tagit slut inte visas som ett löfte kassan inte håller.
 *
 * Tills svaret kommit visas listpriset minus rabatten. Servern kan ge mindre rabatt än så — företagsrabatten har ett
 * marginalgolv sedan 2026-10-15 — så reserven gäller bara de första millisekunderna.
 */
import { useEffect, useMemo, useState } from 'react';
import { businessApi } from '@/lib/business-api';

export interface RadPris { lista: number; pris: number; kampanj: { namn: string } | null; /** Rabatten det faktiskt blev, i procent (marginalgolvet kan ge mindre än avtalet). */ rabatt?: number }

export function useForetagsPriser(items: Array<{ product: { id: number; price: number }; quantity: number }>, rabatt: number) {
  const idn = useMemo(() => items.map((i) => i.product.id).sort((a, b) => a - b).join(','), [items]);
  const [priser, setPriser] = useState<Record<string, RadPris>>({});

  useEffect(() => {
    if (!idn) { setPriser({}); return; }
    let avbruten = false;
    businessApi.getPriser(idn.split(',').map(Number))
      .then((r) => { if (!avbruten) setPriser(r.priser ?? {}); })
      .catch(() => { /* reservberäkningen nedan gäller */ });
    return () => { avbruten = true; };
  }, [idn]);

  return useMemo(() => {
    const rad = (it: { product: { id: number; price: number } }): RadPris => {
      const p = priser[String(it.product.id)];
      if (p) return p;
      const lista = it.product.price / 1.25;
      return { lista, pris: lista * (1 - rabatt / 100), kampanj: null };
    };
    let lista = 0; let netto = 0; let kampanj = false;
    for (const it of items) {
      const r = rad(it);
      lista += r.lista * it.quantity;
      netto += r.pris * it.quantity;
      if (r.kampanj) kampanj = true;
    }
    return { rad, lista, netto, avdrag: lista - netto, kampanj };
  }, [items, priser, rabatt]);
}

/*
 * Ett företagspris för EN artikel (produktkort och produktsida i portalen). Sedan 2026-10-15 kan priset inte räknas i
 * webbläsaren: rabatten har ett marginalgolv (API:ets lib/foretagsrabatt.ts) som bygger på inköpet, och inköpet lämnar
 * aldrig servern. Korten på en sida samlas till ETT anrop (50 ms fönster) och svaren sparas för sidvisningen.
 */
const cache = new Map<number, RadPris>();
const lyssnare = new Map<number, Set<() => void>>();
let vantar = new Set<number>();
let timer: ReturnType<typeof setTimeout> | null = null;

function hamta(id: number) {
  vantar.add(id);
  if (timer) return;
  timer = setTimeout(() => {
    const ids = [...vantar]; vantar = new Set(); timer = null;
    for (let i = 0; i < ids.length; i += 100) {
      const del = ids.slice(i, i + 100);
      businessApi.getPriser(del).then((r) => {
        for (const id of del) {
          const p = r.priser?.[String(id)];
          if (p) cache.set(id, p);
          lyssnare.get(id)?.forEach((f) => f());
        }
      }).catch(() => { /* reservberäkningen gäller */ });
    }
  }, 50);
}

/** Serverns företagspris för artikeln, eller null tills svaret kommit (visa då inget rabatterat pris som löfte). */
export function useForetagsPris(id: number | undefined, aktiv: boolean): RadPris | null {
  const [, rita] = useState(0);
  useEffect(() => {
    if (!aktiv || !id) return;
    const f = () => rita((n) => n + 1);
    if (!lyssnare.has(id)) lyssnare.set(id, new Set());
    lyssnare.get(id)!.add(f);
    if (!cache.has(id)) hamta(id);
    return () => { lyssnare.get(id)?.delete(f); };
  }, [id, aktiv]);
  return aktiv && id ? cache.get(id) ?? null : null;
}
